import { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Sparkle } from 'lucide-react';

import Badge from '../../../Badge.jsx';
import IconHighlight from '../../../IconHighlight.jsx';
import ProgressBar from '../../../ProgressBar.jsx';
import { toneFor } from '../constants/statTones';
import { formatValue, toneForToken, WidgetCarouselNav } from '../shared';

/**
 * A written read of the data — what an agent concluded, and why.
 *
 * This widget **interprets**. Its sibling, `NextBestActionWidget`, **proposes**.
 * They share the single four-point Sparkle because both are agent output, and
 * they are not interchangeable: an interpretation with a button that does
 * something has quietly become a recommendation.
 *
 * ## Reading the contract (CT-5641)
 *
 * `AI_SUMMARY` publishes **one** read, not a list: `summary_text`, the
 * `highlights[]` pulled out of it, when it was `generated_at` and which
 * `provider_ref` produced it. That is the shape this now renders first.
 *
 * - **`highlights[{text, severity}]`** are the sentences that carry the
 *   warning, under their own heading, one line each with the severity's icon.
 *   Not badges: a read has up to three of these and they are sentences, so a
 *   row of pills turns the findings into a tag cloud exactly when there is
 *   most to read. A finding that looks like the rest of the prose is a finding
 *   nobody acts on.
 * - **`context_refs[]`** is the audit trail, printed with each query's `ref`
 *   and `row_count` — "repairs 27 rows · invoices 64 rows". A bare count of
 *   queries cannot answer "what was this read actually built from", which is
 *   the only question the trail exists for. `sources[]` joins the same line as
 *   the number of record fields it drew on; the uuids belong in the detail
 *   panel, not on the card.
 * - **`generated_at`** is shown, always, in the footer beside the provider. An
 *   agent's conclusion with no date is a conclusion the reader cannot weigh —
 *   yesterday's read of a renewal is not the same claim as this morning's.
 * - **`resolution: 'QUERY_REF'`** means the agent did not write prose; it
 *   proposed a query instead. There is nothing to read, so the widget says so
 *   rather than rendering an empty card.
 *
 * The multi-read carousel stays for the pre-v2 `items[]` shape. Several reads
 * coexisting is normal — they are about different *areas* (the renewal, the
 * governance posture) — and `category` is a neutral badge in that shape,
 * never a coloured one: it is an area, not a state.
 *
 * `confidence` is rendered exactly as given. The widget never rounds or
 * rescales it: a number the reader is meant to weigh has to be the agent's own.
 *
 * @param {object} props
 * @param {object} props.data `AI_SUMMARY`'s single read, or the pre-v2 `items[]`.
 * @param {boolean} [props.compact=false] Drop the reasoning paragraph — for a narrow slot.
 * @param {string} [props.className]
 */
function AiSummaryWidget({ data, compact = false, className = '' }) {
  const items = data?.items ?? [];
  const [index, setIndex] = useState(0);

  const step = useCallback(
    (delta) => setIndex((current) => (current + delta + items.length) % items.length),
    [items.length],
  );

  const highlights = useMemo(() => (data?.highlights ?? []).map((highlight, position) => ({
    key: `${position}-${highlight.text}`,
    text: highlight.text,
    tone: toneForToken(highlight.severity),
  })), [data]);

  /*
    What the read was built from, in one line: each context query by its `ref`
    and how many rows it returned, then how many of the record's own fields
    were in scope. Named rather than counted — "2 source queries" is true and
    useless, "repairs 27 rows · invoices 64 rows" is what someone checking the
    agent's work needs.
  */
  const trail = useMemo(() => {
    const parts = (data?.context_refs ?? []).map((ref) => (
      typeof ref.row_count === 'number' ? `${ref.ref} ${ref.row_count} rows` : ref.ref
    ));
    const fields = data?.sources?.length ?? 0;
    if (fields) parts.push(fields === 1 ? '1 record field' : `${fields} record fields`);
    return parts.length ? `Sources: ${parts.join(' · ')}` : '';
  }, [data]);

  // ── The contract's shape: one read ───────────────────────────────────────
  if (data?.summary_text || data?.resolution === 'QUERY_REF') {
    return (
      <div className={`flex min-w-0 flex-col gap-2 rounded-lg border border-edge bg-surface-muted p-3 ${className}`.trim()}>
        <div className="flex min-w-0 items-center gap-1.5">
          <Sparkle size={13} className="shrink-0 text-purple-500 dark:text-purple-300" aria-hidden />
          <span className="truncate text-[11px] font-medium text-content-secondary">
            {data.provider_ref ?? 'Agent'}
          </span>
        </div>

        {data.summary_text ? (
          <p className="m-0 text-[13px] leading-snug text-content">{data.summary_text}</p>
        ) : (
          /* QUERY_REF: the agent proposed a query instead of prose. Saying so
             is more useful than an empty card the reader has to interpret. */
          <p className="m-0 text-[11px] italic leading-relaxed text-content-muted">
            The agent proposed a query rather than a written read.
          </p>
        )}

        {!compact && highlights.length ? (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-content-muted">
              Findings
            </span>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {highlights.map((highlight) => {
                const tone = toneFor(highlight.tone);
                const Icon = tone.defaultIcon;
                return (
                  <li key={highlight.key} className="flex min-w-0 items-start gap-1.5">
                    <IconHighlight
                      icon={Icon}
                      bgColor={tone.bg}
                      iconColor={tone.icon}
                      size="small"
                    />
                    <span className="min-w-0 flex-1 text-[11px] leading-5 text-content">
                      {highlight.text}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {trail || data.generated_at ? (
          <div className="flex min-w-0 flex-col gap-px border-t border-edge pt-1.5">
            {trail ? <span className="text-[10px] text-content-muted">{trail}</span> : null}
            {data.generated_at ? (
              <span className="text-[10px] text-content-muted">
                {formatValue(data.generated_at, { style: 'DATETIME' })}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  // ── The pre-v2 shape: several reads, one at a time ───────────────────────
  if (!items.length) return null;

  const item = items[Math.min(index, items.length - 1)];

  return (
    <div className={`flex flex-col gap-2 ${className}`.trim()}>
      <WidgetCarouselNav
        index={index}
        count={items.length}
        onStep={step}
        previousLabel="Previous read"
        nextLabel="Next read"
      />

      <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-edge bg-surface-muted p-3">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <Sparkle size={13} className="shrink-0 text-purple-500 dark:text-purple-300" aria-hidden />
          <span className="truncate text-[11px] font-medium text-content-secondary">
            {item.agent}
          </span>
          {item.category ? (
            <Badge size="small" variant="neutral">
              {item.category}
            </Badge>
          ) : null}
        </div>

        <p className="m-0 text-[13px] font-semibold leading-snug text-content">{item.headline}</p>

        {!compact && item.detail ? (
          <p className="m-0 text-[11px] leading-relaxed text-content-muted">{item.detail}</p>
        ) : null}

        {item.confidence != null ? (
          <ProgressBar
            value={item.confidence}
            tone="purple"
            label={`${item.confidence}% confidence`}
            ariaLabel="Confidence"
            labelClassName="text-[11px]"
          />
        ) : null}

        {item.sources?.length ? (
          <div className="flex min-w-0 flex-wrap gap-1.5">
            {item.sources.map((source) => (
              <Badge key={source.label} size="small" variant={toneFor(source.tone).badge}>
                {source.label}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

AiSummaryWidget.propTypes = {
  /** `AI_SUMMARY`'s single read. The pre-v2 `items[]` carousel still renders. */
  data: PropTypes.shape({
    resolution: PropTypes.oneOf(['GENERATED_CONTENT', 'QUERY_REF']),
    summary_text: PropTypes.string,
    highlights: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        severity: PropTypes.oneOf(['INFO', 'SUCCESS', 'WARNING', 'DANGER']),
        field_key: PropTypes.string,
      }),
    ),
    sources: PropTypes.arrayOf(
      PropTypes.shape({ field_key: PropTypes.string, record_uuid: PropTypes.string }),
    ),
    query_ref: PropTypes.object,
    context_refs: PropTypes.arrayOf(PropTypes.object),
    generated_at: PropTypes.string,
    provider_ref: PropTypes.string,
    /** The pre-v2 shape, still accepted. */
    items: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default memo(AiSummaryWidget);
